// Pure presentation logic for a row in the doc version-history list. Kept
// separate from the JSX so the label/AI/byline decisions are unit-testable and
// don't depend on locale-formatted dates (the caller passes `dateStr`).

export interface VersionRowInput {
  /** Pre-formatted timestamp string (locale formatting done by the caller). */
  dateStr: string;
  /** Checkpoint label, e.g. "Before AI edit" or a user-typed version name. */
  label?: string;
  author?: 'ai' | 'user' | string;
  type?: 'manual' | 'auto' | 'restore' | 'ai' | string;
  /** The live "Current version" row, which has no checkpoint metadata. */
  isCurrent?: boolean;
}

export interface VersionRowView {
  /** Primary line: the version's name when it has one, else its timestamp. */
  heading: string;
  /** Secondary line: the timestamp, shown only when the heading is a label. */
  timestamp?: string;
  /** Who/what produced it. */
  byline: string;
  /** Whether to badge the row as an AI edit. */
  isAi: boolean;
}

/**
 * Decide what a version-history row should display.
 *
 * - A named/labelled checkpoint shows its label as the heading and demotes the
 *   timestamp to a secondary line; an anonymous autosave shows just the date.
 * - A checkpoint is treated as AI-authored if it's explicitly tagged
 *   (`author:'ai'`/`type:'ai'`) or its label mentions AI as a whole word (so the
 *   "Before AI edit" checkpoint is recognised without relying on tagging, while
 *   names like "Email draft" — which merely contain the letters "ai" — are not).
 */
export function describeVersionRow(input: VersionRowInput, authorName?: string): VersionRowView {
  const label = input.isCurrent ? undefined : input.label?.trim() || undefined;
  const isAi = !input.isCurrent && (
    input.author === 'ai' ||
    input.type === 'ai' ||
    (typeof label === 'string' && /\bAI\b/i.test(label))
  );
  return {
    heading: label || input.dateStr,
    timestamp: label ? input.dateStr : undefined,
    byline: isAi ? 'AI assistant' : (authorName?.trim() || 'You'),
    isAi,
  };
}
